import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function ServiceAreas() {
  const cities = useFetch("/geo/cities", []);
  const areas = useFetch("/geo/service-areas", []);
  const [cityOpen, setCityOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [cityName, setCityName] = useState("");
  const [busy, setBusy] = useState(false);
  const [af, setAf] = useState({ city_id: "", name: "", center_lat: 26.8467, center_lng: 80.9462, radius_km: 15, pincodes: "" });
  const setA = (k, v) => setAf((f) => ({ ...f, [k]: v }));

  const addCity = async () => {
    if (!cityName.trim()) return;
    setBusy(true);
    try { await api.post("/admin/cities", { name: cityName.trim(), state: "Uttar Pradesh", is_active: true }); toast.success("City added"); setCityName(""); setCityOpen(false); cities.refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const addArea = async () => {
    if (!af.city_id || !af.name.trim()) return toast.error("City and name required");
    setBusy(true);
    try {
      await api.post("/admin/service-areas", {
        city_id: af.city_id, name: af.name.trim(), center_lat: Number(af.center_lat), center_lng: Number(af.center_lng),
        radius_km: Number(af.radius_km), pincodes: af.pincodes ? af.pincodes.split(",").map((s) => s.trim()).filter(Boolean) : [], is_active: true,
      });
      toast.success("Service area added"); setAreaOpen(false); areas.refetch();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const cityName_ = (id) => cities.data?.find((c) => c.id === id)?.name || id?.slice(-6);

  return (
    <div>
      <PageHeader title="Service Areas" subtitle="Configure serviceable cities and geofenced areas (Lucknow live)" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold font-outfit">Cities</h3>
            <Dialog open={cityOpen} onOpenChange={setCityOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-city-btn"><Plus className="h-4 w-4 mr-1.5" /> Add City</Button></DialogTrigger>
              <DialogContent data-testid="city-dialog">
                <DialogHeader><DialogTitle>New City</DialogTitle></DialogHeader>
                <div><Label>City name</Label><Input value={cityName} onChange={(e) => setCityName(e.target.value)} placeholder="e.g. Kanpur" className="mt-1.5" data-testid="city-name-input" /></div>
                <DialogFooter><Button variant="outline" onClick={() => setCityOpen(false)}>Cancel</Button><Button onClick={addCity} disabled={busy} data-testid="city-save-btn">{busy ? "Saving…" : "Add"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {cities.loading ? <Loading /> : cities.error ? <ErrorState error={cities.error} onRetry={cities.refetch} /> :
            !cities.data?.length ? <SectionCard className="p-2"><EmptyState label="No cities" /></SectionCard> : (
              <div className="space-y-2" data-testid="cities-list">
                {cities.data.map((c) => (
                  <SectionCard key={c.id} className="p-3 flex items-center justify-between" data-testid={`city-${c.id}`}>
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><span className="font-medium">{c.name}</span><span className="text-xs text-muted-foreground">{c.state}</span></div>
                    <StatusChip status={c.is_active ? "ACTIVE" : "INACTIVE"} tone={c.is_active ? "green" : "gray"} />
                  </SectionCard>
                ))}
              </div>
            )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold font-outfit">Service Areas</h3>
            <Dialog open={areaOpen} onOpenChange={setAreaOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-area-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Area</Button></DialogTrigger>
              <DialogContent data-testid="area-dialog">
                <DialogHeader><DialogTitle>New Service Area</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>City</Label>
                    <Select value={af.city_id} onValueChange={(v) => setA("city_id", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="area-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent>{cities.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Area name</Label><Input value={af.name} onChange={(e) => setA("name", e.target.value)} placeholder="e.g. Gomti Nagar" className="mt-1.5" data-testid="area-name-input" /></div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label>Lat</Label><Input type="number" value={af.center_lat} onChange={(e) => setA("center_lat", e.target.value)} className="mt-1.5" data-testid="area-lat-input" /></div>
                    <div><Label>Lng</Label><Input type="number" value={af.center_lng} onChange={(e) => setA("center_lng", e.target.value)} className="mt-1.5" data-testid="area-lng-input" /></div>
                    <div><Label>Radius km</Label><Input type="number" value={af.radius_km} onChange={(e) => setA("radius_km", e.target.value)} className="mt-1.5" data-testid="area-radius-input" /></div>
                  </div>
                  <div><Label>Pincodes (comma-separated, optional)</Label><Input value={af.pincodes} onChange={(e) => setA("pincodes", e.target.value)} placeholder="226001, 226010" className="mt-1.5" data-testid="area-pincodes-input" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setAreaOpen(false)}>Cancel</Button><Button onClick={addArea} disabled={busy} data-testid="area-save-btn">{busy ? "Saving…" : "Add"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {areas.loading ? <Loading /> : areas.error ? <ErrorState error={areas.error} onRetry={areas.refetch} /> :
            !areas.data?.length ? <SectionCard className="p-2"><EmptyState label="No service areas" /></SectionCard> : (
              <div className="space-y-2" data-testid="areas-list">
                {areas.data.map((a) => (
                  <SectionCard key={a.id} className="p-3" data-testid={`area-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{a.name}</span>
                      <StatusChip status={a.is_active ? "ACTIVE" : "INACTIVE"} tone={a.is_active ? "green" : "gray"} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{cityName_(a.city_id)} · {a.center_lat}, {a.center_lng} · {a.radius_km}km {a.pincodes?.length ? `· ${a.pincodes.length} pincodes` : ""}</div>
                  </SectionCard>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
